import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FaOm, FaMapMarkerAlt, FaClock, FaPrayingHands, FaUsers, FaVideo, FaCalendar, FaStar, FaArrowLeft } from 'react-icons/fa';
import Footer from '../../component/common/Footer';
import { TEMPLE_DETAILS } from '../../Utils/Constants';

const Lokhnath = () => {
  const navigate = useNavigate();
  const templeData = TEMPLE_DETAILS['lokhnath'];

  const handleBackToTemples = () => {
    navigate('/temples');
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(<FaStar key={i} className="text-yellow-400" />);
    }

    if (hasHalfStar) {
      stars.push(<FaStar key="half" className="text-yellow-400" />);
    }

    return stars;
  };

  const handleRelatedTempleClick = (templeId) => {
    navigate(`/temples/${templeId}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50">
      {/* Header */}
      <header className="bg-white shadow-lg border-b-2 border-orange-200">
        <nav className="container mx-auto px-4 py-4 flex justify-between items-center">
          <button
            onClick={handleBackToTemples}
            className="flex items-center space-x-3 text-orange-800 hover:text-orange-600 transition-colors"
          >
            <FaArrowLeft className="text-xl" />
            <span className="text-lg font-semibold">Back to Temples</span>
          </button>
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-full flex items-center justify-center">
              <FaOm className="text-white text-xl" />
            </div>
            <span className="text-2xl font-bold text-orange-800">Anant Aaradhya</span>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="relative h-[400px] mt-8">
        <img
          src={templeData.heroImage}
          alt={templeData.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center text-center">
          <div className="text-white">
            <h1 className="text-4xl md:text-6xl font-bold mb-4">{templeData.name}</h1>
            <p className="text-lg md:text-xl text-orange-100">{templeData.location} | 🕉️ {templeData.deity}</p>
            <div className="flex justify-center mt-3 space-x-1 text-yellow-400">
              {renderStars(templeData.rating)}
              <span className="ml-2 text-white">({templeData.rating})</span>
            </div>
          </div>
        </div>
      </section>

      {/* Temple Info */}
      <section className="py-12 container mx-auto px-4">
        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <h2 className="text-3xl font-bold text-orange-800 mb-4">About the Temple</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              {templeData.description}
            </p>
            <div className="space-x-3">
              <button className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-5 py-3 rounded-full font-semibold hover:from-orange-600 hover:to-red-600 transition-all">
                <FaVideo className="inline mr-2" />
                Watch Live
              </button>
              <button className="bg-white text-orange-600 border-2 border-orange-500 px-5 py-3 rounded-full font-semibold hover:bg-orange-50 transition-all">
                <FaCalendar className="inline mr-2" />
                Book Puja
              </button>
            </div>
          </div>
          <div>
            <h2 className="text-3xl font-bold text-orange-800 mb-4">Temple Details</h2>
            <ul className="space-y-4 text-gray-700">
              <li className="flex items-center">
                <FaMapMarkerAlt className="text-orange-600 mr-3" />
                {templeData.location}
              </li>
              <li className="flex items-center">
                <FaClock className="text-orange-600 mr-3" />
                {templeData.timings}
              </li>
              <li className="flex items-center">
                <FaPrayingHands className="text-orange-600 mr-3" />
                Dedicated to: {templeData.deity}
              </li>
              <li className="flex items-center">
                <FaUsers className="text-orange-600 mr-3" />
                {templeData.visitors}
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-orange-800 mb-6 text-center">Location Map</h2>
          <div className="rounded-xl overflow-hidden shadow-lg">
            <iframe
              src={templeData.mapEmbed}
              width="100%"
              height="400"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              title={`${templeData.name} Location`}
            ></iframe>
          </div>
        </div>
      </section>

      {/* Related Temples */}
      <section className="py-12 bg-gradient-to-br from-orange-50 to-amber-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-orange-800 mb-8 text-center">You May Also Like</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {templeData.relatedTemples.map((temple) => (
              <div
                key={temple.id}
                onClick={() => handleRelatedTempleClick(temple.id)}
                className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all cursor-pointer group"
              >
                <img
                  src={temple.image}
                  alt={temple.name}
                  className="h-40 w-full object-cover group-hover:scale-105 transition-transform"
                />
                <div className="p-4">
                  <h3 className="font-bold text-orange-800 text-lg">{temple.name}</h3>
                  <p className="text-sm text-gray-600">{temple.location}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Lokhnath;
